<script setup>
import {computed, ref} from 'vue';
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";

const props = defineProps({
  seoAgentName: {
    type: String,
    required: false,
    default: '',
  },
  seoAgentIcon: {
    type: String,
    required: false,
    default: '',
  },
  seoAgentIconUrl: {
    type: String,
    required: false,
    default: '',
  },
});

const seoAgentName = ref(props.seoAgentName);
const agentImageFile = ref(null);
const errors = ref({});
const seoAgentIconPreviewUrl = ref(props.seoAgentIconUrl);

const loading = computed(() => loadingSubmit.value || loadingDelete.value);

const {mutate: deleteImage, isPending: loadingDelete} = useMutation({
  mutationFn: async () => {
    errors.value = {};
    return await axios.delete(`/ajax/v2/whitelabel/seo-agent-settings/icon/delete`);
  },
  onError: () => {
    errorToast('Something went wrong, please try again.');
  },
  onSettled: () => {
    seoAgentIconPreviewUrl.value = null;
    successToast('Your seo agent icon has been deleted successfully.');
  },
});

const {mutate: submit, isPending: loadingSubmit} = useMutation({
  mutationFn: async () => {
    errors.value = {};

    return await axios.post(
      `/ajax/v2/whitelabel/seo-agent-settings`,
      buildFormData(),
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },
  onError: (error) => {
    if (error.response.status === 422) {
      errors.value = error.response.data.errors;
      return;
    }

    if (error?.response?.status === 413) {
      errors.value = {
        seo_agent_icon: ['The seo agent icon field must not be greater than 3MB'],
      };

      return;
    }

    errorToast('Something went wrong, please try again.');
  },
  onSuccess: ({data}) => {
    seoAgentIconPreviewUrl.value = data.seo_agent_icon_url;

    successToast('Your seo agent branding has been updated successfully.');
  },
});

const handleImageChange = (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    return;
  }

  if (file.size > 3000000) {
    errorToast('The icon must be smaller than 3MB.');
    event.target.value = '';
    return;
  }

  agentImageFile.value = file;
};

const buildFormData = () => {
  const formData = new FormData();
  formData.append('seo_agent_name', seoAgentName.value ?? '');

  if (agentImageFile.value) {
    formData.append('seo_agent_icon', agentImageFile.value);
  }

  return formData;
};
</script>

<template>
  <div class="card">
    <div class="card-block">
      <div class="row">
        <div class="col-md-4">
          <p class="mrg-top-10 text-dark">
            <b>
              Agent Name <br>
              <small>
                Set the display name for your SEO Agent. This name will appear throughout the platform wherever the
                Agent completes tasks or provides SEO recommendations.
              </small>
            </b>
          </p>
        </div>
        <div class="col-md-8">
          <input
            v-model="seoAgentName"
            class="form-control"
            :class="{
              'error': errors.seo_agent_name
            }"
            placeholder="“Hike SEO” or “Your SEO Assistant”"
            name="seo_agent_name"
            type="text"
          >
          <div
            v-if="errors.seo_agent_name"
            class="text-danger mt-2"
          >
            {{ errors.seo_agent_name[0] }}
          </div>
        </div>
      </div>

      <div class="row settings-form-row">
        <div class="col-md-4">
          <p class="mrg-top-10 text-dark">
            <b>
              Agent Icon <br>
              <small>
                Upload the icon used to represent your SEO Agent. This icon appears in areas such as the approval
                centre, changelog and automated task updates.
              </small>
              <br>
              <small><strong>Recommended size:</strong> 64 × 64px</small><br>
              <small><strong>Maximum file size:</strong> 3MB</small>
            </b>
          </p>
        </div>
        <div class="col-md-8">
          <div
            v-if="seoAgentIconPreviewUrl"
            class="d-flex align-items-center"
          >
            <img
              :src="seoAgentIconPreviewUrl"
              width="64px"
              height="64px"
              style="object-fit: cover; border-radius: 4px; background: #f0f0f0;"
            >
            <button
              type="button"
              class="btn btn-sm btn-danger"
              style="margin-left: 10px; margin-top: 8px;"
              @click="deleteImage"
            >
              Delete Image
            </button>
          </div>
          <div v-else>
            <input
              type="file"
              accept="image/*"
              class="form-control"
              @change="handleImageChange"
            >
          </div>
          <div
            v-if="errors.seo_agent_icon"
            class="text-danger mt-2"
          >
            {{ errors.seo_agent_icon[0] }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <p class="text-center">
    <button
      :disabled="loading"
      class="btn btn-success font-weight-bold"
      @click="submit"
    >
      Update Information
    </button>
  </p>
</template>


